import React, { useRef, useState, useEffect, FC } from 'react';
import styled from 'styled-components';
import { Icons } from '@assets';
import { ISelector } from '@types';
import { colors } from '@styles';
import { flexMixin, textMixin } from '@styles/mixins';

interface IProps {
    options: ISelector[];
    setSelectValue: any;
    value: ISelector;
}

const BookingPeriodSelector: FC<IProps> = props => {
    const { options, setSelectValue, value } = props;
    const [switchIsOpen, setSwitchIsOpen] = useState<boolean>(false);

    const dropdownRef = useRef<HTMLDivElement>(null);

    const switchHandler = (key: ISelector) => {
        if (key.value !== value.value) {
            setSelectValue(key);
        }
        setSwitchIsOpen(false);
    };

    const handleTitleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSwitchIsOpen(prev => !prev);
    };

    const handleOpenSelect = () => {
        setSwitchIsOpen(prev => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setSwitchIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    return (
        <SelectorWrap $isOpen={switchIsOpen}>
            <TitleLabel onClick={handleTitleClick}>{value.label}</TitleLabel>
            <NoiseSelectorBtn onClick={handleOpenSelect}>
                {switchIsOpen ? (
                    <img src={Icons.IconArrowUp} alt="Proga.pro arrow" />
                ) : (
                    <img src={Icons.IconArrowDown} alt="Proga.pro arrow down" />
                )}
            </NoiseSelectorBtn>
            {switchIsOpen && (
                <SelectorDropdown $isOpen={switchIsOpen} ref={dropdownRef}>
                    {options?.map(item => {
                        if (item.value === value.value) return null;

                        return (
                            <React.Fragment key={item.key}>
                                <DropdownItem
                                    onClick={() => switchHandler(item)}
                                >
                                    <span>{item.label}</span>
                                </DropdownItem>
                            </React.Fragment>
                        );
                    })}
                </SelectorDropdown>
            )}
        </SelectorWrap>
    );
};

export default BookingPeriodSelector;

const SelectorWrap = styled.div<{
    $isOpen: boolean;
}>`
    width: 100%;
    background: ${colors.cleanWhite};
    border-radius: 8px 8px 0 0;
    position: relative;
    ${flexMixin('column', 'flex-start', 'center')}
    ${textMixin({
        fontSize: '13px',
        fontWeight: 600,
    })};
    line-height: 26px;
    color: #1f1e24;
    cursor: pointer;
`;

const TitleLabel = styled.button`
    font-style: normal;
    ${textMixin({
        fontSize: '30px',
        fontWeight: 600,
    })};
    line-height: 34px;
    width: 100%;
    height: 38px;
    ${flexMixin('column', 'flex-start', 'flex-start')}
    color: ${colors.grey};
    background: none;
    padding-left: 17px;
    border: none;

    & span {
        ${textMixin({
            fontSize: '14px',
            fontWeight: 500,
        })};
        line-height: 28px;
        color: ${colors.grey};
    }
`;

const SelectorDropdown = styled.div<{ $isOpen: boolean }>`
    width: 100%;
    position: absolute;
    z-index: 8;
    top: 50px;
    left: 0;
    ${flexMixin('column', 'center', 'center')}
    background-color:  ${colors.cleanWhite};
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    box-shadow: ${({ $isOpen }) =>
        $isOpen
            ? '0px 8px 8px 0px  rgb(158 158 158 / 40%)'
            : '0px 0px 8px 0px rgb(158 158 158 / 40%)'};
    ::-webkit-scrollbar {
        width: 14px;
        background-color: inherit;
    }
    ::-webkit-scrollbar-thumb {
        height: 140px;
        background-color: #e7e7e7;
    }
`;

const DropdownItem = styled.button`
    width: 100%;
    ${flexMixin('column', 'flex-start', 'flex-start')}
    background: none;
    border: none;
    padding: 8px 0 8px 17px;
    cursor: pointer;

    & span {
        ${textMixin({
            fontSize: '14px',
            fontWeight: 500,
        })};
        line-height: 28px;
        color: ${colors.grey};
    }

    &:hover {
        background-color: #e6b3cf;
    }
`;

const NoiseSelectorBtn = styled.div`
    position: absolute;
    bottom: 5px;
    right: 6px;
    width: 30px;
    padding-top: 10px;
    height: 100%;
    ${flexMixin('column', 'center', 'center')}
    & svg path {
        fill: #a7a7a7;
    }
`;
